import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ProductCard from "@/components/base/ProductCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { products } from "@/mocks/products";
import { useUI } from "@/context/UIContext";

const RING_SIZES = ["48", "50", "52", "54", "56", "58"];
const FINISHES = ["Polished", "Brushed"];

export default function ProductDetail() {
  const { slug } = useParams();
  const product = products.find((p) => p.slug === slug) || products[0];
  const { addToCart } = useUI();

  const variantOptions = useMemo(() => {
    if (product.category === "Rings") return { label: "Ring Size", options: RING_SIZES };
    return { label: "Finish", options: FINISHES };
  }, [product.category]);

  const [variant, setVariant] = useState(variantOptions.options[0]);
  const [qty, setQty] = useState(1);
  const [openAcc, setOpenAcc] = useState<string | null>("details");
  const [activeImg, setActiveImg] = useState(0);

  const onSale = typeof product.compareAt === "number" && product.compareAt > product.price;
  const salePct = onSale ? Math.round((1 - product.price / (product.compareAt as number)) * 100) : 0;

  const related = products.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 4);
  const filler = products.filter((p) => p.id !== product.id).slice(0, 4);
  const also = (related.length === 4 ? related : filler).slice(0, 4);

  const gallery = [
    { src: product.image, label: "Packshot" },
    { src: product.secondary || product.image, label: "Second angle" },
    { src: product.wearing || product.image, label: "On-body" },
    { src: product.detail || product.image, label: "Detail" },
  ];

  const toggle = (k: string) => setOpenAcc(openAcc === k ? null : k);

  const accItems = [
    { key: "details", title: "Details", body: `${product.description} Presented in the Aurelin cloth pouch and archival box.` },
    { key: "materials", title: "Materials", body: `${product.material}. Certified recycled precious metal. Nickel-free, hypoallergenic.` },
    { key: "care", title: "Care", body: "Store in the Aurelin pouch. Avoid direct contact with perfume, lotion or chlorine. Polish gently with the included cloth." },
    { key: "shipping", title: "Shipping & Returns", body: "Complimentary insured shipping worldwide. Made-to-order and dispatched from Antwerp within 5–7 business days. 30-day returns on unworn pieces." },
  ];

  return (
    <Layout>
      <section>
        <div className="shell pt-10 pb-10">
          <Breadcrumb items={[
            { label: "Shop", to: "/shop" },
            { label: product.category, to: `/shop?category=${product.category}` },
            { label: product.name },
          ]} />
        </div>

        <div className="shell pb-24 grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-10">
          <div className="md:col-span-7">
            <div className="flex gap-3">
              <div className="hidden md:flex flex-col gap-3 w-[80px] shrink-0">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    aria-label={g.label}
                    style={{ backgroundColor: "#F4F1EC" }}
                    className={`w-[80px] aspect-square overflow-hidden cursor-pointer border transition-all duration-200 ${activeImg === i ? "border-black opacity-100" : "border-transparent opacity-65 hover:opacity-100 hover:border-black/20"}`}
                  >
                    <img src={g.src} alt={g.label} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
              <div className="flex-1 relative">
                <div className="w-full aspect-square overflow-hidden" style={{ backgroundColor: "#F4F1EC" }}>
                  <img key={activeImg} src={gallery[activeImg].src} alt={`${product.name} — ${gallery[activeImg].label}`} title={`${product.name} — ${product.material}`} decoding="async" className="w-full h-full object-cover object-top" style={{ animation: "img-soft-in 320ms var(--ease-brand) both" }} />
                </div>
                {onSale && (
                  <span className="absolute top-4 left-4 text-white text-[13px] leading-none px-3 py-2" style={{ backgroundColor: "#BD4C2A" }}>-{salePct}%</span>
                )}
                <div className="md:hidden mt-3 flex gap-3 overflow-x-auto">
                  {gallery.map((g, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} style={{ backgroundColor: "#F4F1EC" }} className={`shrink-0 w-[64px] aspect-square overflow-hidden cursor-pointer border ${activeImg === i ? "border-black" : "border-transparent"}`}>
                      <img src={g.src} alt={g.label} className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="md:sticky md:top-24">
              <p className="eyebrow mb-4">{product.category}</p>
              <h1 className="font-display text-4xl md:text-5xl text-black leading-tight">{product.name}</h1>
              {onSale ? (
                <p className="mt-3 text-xl flex items-baseline gap-3">
                  <span style={{ color: "#BD4C2A" }}>${product.price.toLocaleString()}</span>
                  <span className="text-[15px] text-graphite/70 line-through">${(product.compareAt as number).toLocaleString()}</span>
                  <span className="text-[12px] text-white px-2 py-1 leading-none" style={{ backgroundColor: "#BD4C2A" }}>-{salePct}%</span>
                </p>
              ) : (
                <p className="mt-3 text-xl text-black">${product.price.toLocaleString()}</p>
              )}
              <div className="mt-2 flex items-center gap-2 text-[13px] text-graphite">
                <span className="text-brass"><i className="ri-star-fill"></i></span>
                <span>{product.rating} · {product.reviews} reviews</span>
              </div>

              <p className="mt-6 text-[15px] text-graphite leading-[1.7]">{product.description}</p>

              <div className="mt-6 text-[13px] text-graphite">
                <span className="text-black">Material · </span>{product.material}
              </div>

              <div className="mt-6">
                <p className="eyebrow mb-3">{variantOptions.label}</p>
                <div className="flex flex-wrap gap-2">
                  {variantOptions.options.map((v) => (
                    <button
                      key={v}
                      onClick={() => setVariant(v)}
                      className={`h-11 min-w-[44px] px-4 text-[13px] whitespace-nowrap cursor-pointer border transition-colors duration-200 ${
                        variant === v ? "text-[#1E144D]" : "border-black/15 text-graphite hover:border-[#777]"
                      }`}
                      style={variant === v ? { borderColor: "#1E144D" } : undefined}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <p className="eyebrow mb-3">Quantity</p>
                <div className="inline-flex items-center border border-black/15">
                  <button className="w-11 h-11 flex items-center justify-center cursor-pointer transition-colors duration-150 hover:bg-[#F2F2F2] active:opacity-70" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease">
                    <i className="ri-subtract-line text-[14px]"></i>
                  </button>
                  <span className="w-10 text-center text-[13px]">{qty}</span>
                  <button className="w-11 h-11 flex items-center justify-center cursor-pointer transition-colors duration-150 hover:bg-[#F2F2F2] active:opacity-70" onClick={() => setQty(qty + 1)} aria-label="Increase">
                    <i className="ri-add-line text-[14px]"></i>
                  </button>
                </div>
              </div>

              <button
                onClick={() => addToCart(product, variant, qty)}
                className="mt-6 w-full h-12 text-canvas text-[13px] font-medium tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200"
                style={{ backgroundColor: "#1E144D" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#2A2061")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1E144D")}
              >
                Add to Bag
              </button>

              <p className="mt-4 text-[13px] text-graphite leading-relaxed">
                {product.stock === "low_stock" ? (
                  <span style={{ color: "#BD4C2A" }}>Low stock · </span>
                ) : (
                  <span className="text-black">{product.stock === "in_stock" ? "In stock" : "Made to order"} · </span>
                )}
                Ships from Antwerp within 5–7 days. Complimentary shipping worldwide.
              </p>

              <div className="mt-10 border-t border-stone">
                {accItems.map((a) => (
                  <div key={a.key} className="border-b border-stone">
                    <button
                      onClick={() => toggle(a.key)}
                      className="w-full flex items-center justify-between py-5 text-left cursor-pointer"
                      aria-expanded={openAcc === a.key}
                    >
                      <span className="text-[12px] font-medium tracking-[0.16em] uppercase text-black">{a.title}</span>
                      <span className="w-5 h-5 flex items-center justify-center text-graphite transition-transform duration-200" style={{ transform: openAcc === a.key ? "rotate(180deg)" : "rotate(0)" }}>
                        <i className={`ri-${openAcc === a.key ? "subtract" : "add"}-line text-[14px]`}></i>
                      </span>
                    </button>
                    <div className={`acc-body ${openAcc === a.key ? "open" : ""}`}>
                      <p className="pb-6 text-[14px] text-graphite leading-[1.7]">{a.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-stone">
          <div className="shell py-24 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="w-full aspect-[4/5] overflow-hidden">
              <img src={product.wearing || product.image} alt={`${product.name} on-body`} title={`${product.name} — worn`} loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="eyebrow mb-4">On the Body</p>
              <h3 className="font-display text-4xl leading-tight text-black">A quiet weight, a considered scale.</h3>
              <p className="mt-6 text-graphite leading-[1.9] text-[15px] max-w-lg">
                Sized against the collarbone and hand of our fit model. Each piece is proportioned so it holds
                its silhouette across a full day of wear — from morning coffee to evening light.
              </p>
            </div>
          </div>
        </div>

        <div className="brand-satin">
          <div className="shell py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow mb-3" style={{color:'rgba(253,253,253,0.7)'}}>Material & Quality</p>
              <h3 className="font-display text-3xl text-canvas">Certified, traceable, forever.</h3>
              <div className="brand-hairline mt-6 max-w-[100px]"></div>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-10 text-sm text-canvas/75 leading-relaxed">
              <div><p className="text-brass mb-2 text-[11px] tracking-[0.22em] uppercase">Material</p><p>{product.material}, certified recycled.</p></div>
              <div><p className="text-brass mb-2 text-[11px] tracking-[0.22em] uppercase">Weight</p><p>3.4g — considered wearable weight.</p></div>
              <div><p className="text-brass mb-2 text-[11px] tracking-[0.22em] uppercase">Origin</p><p>Designed and hand-finished in Antwerp.</p></div>
              <div><p className="text-brass mb-2 text-[11px] tracking-[0.22em] uppercase">Care</p><p>Store in the Aurelin pouch, avoid perfume contact.</p></div>
            </div>
          </div>
        </div>

        <div className="bg-stone">
          <div className="pt-24 pb-24">
            <div className="shell mb-10">
              <p className="eyebrow mb-3">Reviews</p>
              <h3 className="font-display text-3xl text-black">{product.rating} out of 5 · {product.reviews} reviews</h3>
            </div>
            <ReviewMarquee />
          </div>
        </div>

        <div className="shell py-24">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3">Wear It With</p>
            <h2 className="font-display text-4xl text-black">Considered companions</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-product-shop>
            {also.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>
    </Layout>
  );
}

const REVIEWS = [
  { name: "Camille R.", text: "Exactly the quiet piece I had hoped for. Wears beautifully every day." },
  { name: "Sofia M.", text: "The finish is remarkable — soft, warm, and clearly hand-made." },
  { name: "Elena V.", text: "A considered gift. Packaging and craftsmanship both exceptional." },
  { name: "Anaya P.", text: "Weighty in the hand, effortless on the body. Feels like an heirloom already." },
  { name: "Margaux L.", text: "The gold has a warmth I hadn’t expected. It layers beautifully with what I already own." },
  { name: "Ines D.", text: "Quiet, precise, and clearly considered. I wear it from morning to evening." },
  { name: "Hana K.", text: "Arrived in the loveliest packaging. The piece itself is impeccable." },
  { name: "Livia T.", text: "A confident, restrained design. Exactly what I look for in fine jewelry." },
];

function ReviewMarquee() {
  const items = [...REVIEWS, ...REVIEWS];
  return (
    <div className="marquee" aria-hidden="false">
      <div className="marquee-track">
        {items.map((r, i) => (
          <div key={i} className="w-[340px] shrink-0 bg-canvas border border-black/5 p-6">
            <div className="text-brass text-xs">
              <i className="ri-star-fill"></i> <i className="ri-star-fill"></i> <i className="ri-star-fill"></i> <i className="ri-star-fill"></i> <i className="ri-star-fill"></i>
            </div>
            <p className="mt-4 text-[14px] text-graphite leading-relaxed">“{r.text}”</p>
            <p className="mt-6 text-[13px] text-black">{r.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}